import { useUserData } from 'auth'
import { ScrollableTabList, ScrollableTabs } from 'components/scrollableTabs'
import SectionHead from 'components/sectionHead'
import { useSearchParamState } from 'helperFunctions/react'
import { useMemo } from 'react'
import { Route, Routes, useNavigate } from 'react-router'
import { SizableText, Tabs, View } from 'tamagui'
import AddUserPage from '../users/addUserPage'
import EditUser from '../users/editUser'
import UsersPage from '../users/usersPage'
import AutomationSettings from './automation/automationSettings'
import BillingSettings from './billing'
import OrgDetails from './orgDetails'
import XeroIntegration from './xeroIntegration'

const SettingsTabs = () => {
    const { user } = useUserData()
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useSearchParamState('tab', 'Organisation Settings')

    // A null component means the tab is a route link rather than inline content.
    const tabList: [string, any][] = useMemo(
        () => [
            ['Organisation Settings', OrgDetails],
            ['Users', null],
            ['Xero', XeroIntegration],
            //['Links', LinkSettings],
            ['Automation', AutomationSettings],
            ['Billing', BillingSettings],
        ],
        [user.userId],
    )

    return (
        <View
            paddingHorizontal="$6"
            $sm={{ paddingHorizontal: '$4' }}
        >
            <SectionHead title="Settings" />
            <ScrollableTabs
                value={activeTab}
                onChange={val => (val === 'Users' ? navigate('users') : setActiveTab(val as any))}
            >
                <ScrollableTabList>
                    {tabList.map(([label]) => {
                        const tourId =
                            label === 'Organisation Settings'
                                ? 'settings-tab-org'
                                : label === 'Users'
                                  ? 'settings-tab-users'
                                  : label === 'Xero'
                                    ? 'settings-tab-xero'
                                    : undefined
                        const helpId =
                            label === 'Organisation Settings'
                                ? 'settings-tab-org'
                                : label === 'Users'
                                  ? 'settings-tab-users'
                                  : label === 'Xero'
                                    ? 'settings-tab-xero'
                                    : label === 'Billing'
                                      ? 'settings-tab-billing'
                                      : undefined
                        const isActive = activeTab === label
                        return (
                            <Tabs.Tab
                                key={label}
                                value={label}
                                backgroundColor="transparent"
                                borderBottomWidth={2}
                                borderBottomColor={isActive ? '$primary' : 'transparent'}
                                borderRadius={0}
                                marginBottom={-1}
                                paddingVertical={10}
                                paddingHorizontal={14}
                                height="auto"
                                hoverStyle={{ backgroundColor: 'transparent' }}
                                pressStyle={{ backgroundColor: 'transparent', opacity: 0.8 }}
                                animation="quick"
                                {...(tourId ? { 'data-tour': tourId } : {})}
                                {...(helpId ? { 'data-help-id': helpId } : {})}
                            >
                                <SizableText
                                    size="$2"
                                    fontWeight="500"
                                    color={isActive ? '$color' : '$colorMuted'}
                                >
                                    {label}
                                </SizableText>
                            </Tabs.Tab>
                        )
                    })}
                </ScrollableTabList>
            </ScrollableTabs>
            <View
                paddingVertical="$5"
                paddingTop="$5"
            >
                {tabList
                    .filter(([, Component]) => Component != null)
                    .map(([name, Component]) => (
                        <View
                            key={name}
                            display={activeTab == name ? undefined : 'none'}
                        >
                            <Component />
                        </View>
                    ))}
            </View>
        </View>
    )
}

export default function SettingsPage() {
    return (
        <Routes>
            <Route
                path="users"
                element={<UsersPage />}
            />
            <Route
                path="users/new"
                element={<AddUserPage />}
            />
            <Route
                path="users/:userId"
                element={<EditUser />}
            />
            <Route
                path="*"
                element={<SettingsTabs />}
            />
        </Routes>
    )
}
